import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/clients", label: "Clients" },
  { to: "/company", label: "Company" },
  { to: "/blog", label: "Blog" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

const servicesLinks = [
  { to: "/web-design", label: "Web Design & Development" },
  { to: "/ecommerce", label: "Ecommerce Development" },
  { to: "/maintenance", label: "Maintenance Services" },
  { to: "/enterprise-app", label: "Enterprise Application" },
  { to: "/custom-web-app", label: "Custom Web Applications" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white shadow-sm'
      }`}>
        <nav className="container mx-auto flex items-center justify-between py-4 px-4 lg:px-6">
          {/* Logo */}
          <Link to="/" onClick={scrollToTop} className="logo-link">
            <div className="relative0">
              <img 
                src="/images/logo.png" 
                alt="Intense Infotech Logo" 
                className="h-25 w-auto object-contain logo-img"
              />
            </div>
           
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Services Dropdown */}
            <div className="relative group">
              <button
                className="relative px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-1"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <span>Services</span>
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Services Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 transition-all duration-300 z-50 ${
                servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <div className="p-4">
                  <div className="grid grid-cols-1 gap-2">
                    {servicesLinks.map((service) => (
                      <Link
                        key={service.to}
                        to={service.to}
                        onClick={scrollToTop}
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-all duration-200"
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Other Navigation Links */}
            {navLinks.filter(link => link.label !== 'Home').map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={scrollToTop}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? "text-blue-600"
                      : "text-gray-700 hover:text-blue-600"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/contact"
              onClick={scrollToTop}
              className="text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              style={{ backgroundColor: '#11354d' }}
              onMouseEnter={(e) => (e.target as HTMLAnchorElement).style.backgroundColor = '#0d2a3a'}
              onMouseLeave={(e) => (e.target as HTMLAnchorElement).style.backgroundColor = '#11354d'}
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button - Enhanced Hamburger */}
          <button
            className="lg:hidden relative p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 hover:bg-gray-50"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center space-y-1">
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ease-in-out ${
                menuOpen ? 'rotate-45 translate-y-1.5' : 'translate-y-0'
              }`}></span>
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ease-in-out ${
                menuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
              }`}></span>
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ease-in-out ${
                menuOpen ? '-rotate-45 -translate-y-1.5' : 'translate-y-0'
              }`}></span>
            </div>
          </button>
        </nav>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-500 ease-in-out overflow-hidden ${
          menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-white border-t border-gray-100 shadow-xl">
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col space-y-2">
                {/* Services Section */}
                <div className="mb-4">
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:text-blue-600 transition-all duration-200 rounded-lg hover:bg-gray-50"
                  >
                    <span>Services</span>
                    <svg
                      className={`w-5 h-5 transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : 'rotate-0'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={mobileServicesOpen ? "M20 12H4" : "M12 4v16m8-8H4"}
                      />
                    </svg>
                  </button>
                  
                  {/* Mobile Services Links */}
                  <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    mobileServicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="mt-2 space-y-1">
                      {servicesLinks.map((service) => (
                        <Link
                          key={service.to}
                          to={service.to}
                          className="block px-6 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-all duration-200 ml-4"
                          onClick={() => {
                            setMenuOpen(false);
                            setMobileServicesOpen(false);
                            scrollToTop();
                          }}
                        >
                          {service.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Main Navigation */}
                <div className="mb-4">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Navigation
                  </div>
                  {navLinks.filter(link => link.label !== 'Home').map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) =>
                        `block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                          isActive
                            ? "bg-blue-50 text-blue-600 border-l-4 border-blue-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                        }`
                      }
                      onClick={() => {
                        setMenuOpen(false);
                        scrollToTop();
                      }}
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="pt-4 border-t border-gray-100">
                  <Link
                    to="/contact"
                    className="block w-full text-white px-6 py-4 rounded-lg font-semibold text-sm text-center hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                    style={{ backgroundColor: '#11354d' }}
                    onMouseEnter={(e) => (e.target as HTMLAnchorElement).style.backgroundColor = '#0d2a3a'}
                    onMouseLeave={(e) => (e.target as HTMLAnchorElement).style.backgroundColor = '#11354d'}
                    onClick={() => {
                      setMenuOpen(false);
                      scrollToTop();
                    }}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-20"></div>
    </>
  );
}
